import type { MouseEvent } from "react"
import { navigateWindowLocation } from "@theconversation/shared-libraries/windowLocation"

// Gives in-flight tracking requests a chance to be enqueued before the browser
// kills off outstanding JS on navigation.
const NAVIGATION_DELAY_MS = 5

const LEFT_BUTTON = 0
const MIDDLE_BUTTON = 1

/**
 * Click handler for a tracked link/button that also needs to navigate.
 *
 * Emits `onEngage` for tracking, then defers navigation by 5ms via
 * `navigateWindowLocation` so those tracking requests get a chance to be
 * enqueued before the browser kills off in-flight JS on navigation.
 *
 * The deferred navigation only kicks in for plain left-clicks: a
 * modifier-click (cmd/ctrl/shift/alt) or middle-click is left untouched so
 * the browser's native new-tab/new-window behaviour keeps working, and a
 * click inside the PageBuilder editor doesn't get forced out of it.
 *
 * Bind the returned handler to both `onClick` and `onAuxClick`: browsers fire
 * `auxclick`, not `click`, for the middle button, so middle-click engagements
 * would otherwise go untracked. Right-clicks (which also fire `auxclick`) are
 * ignored, since opening the context menu isn't an engagement.
 */
export const useTrackLinkClick = (href: string, analyticsId: string, onEngage: () => void) => {
  return (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (!analyticsId || analyticsId.length === 0) {
      return
    }

    if (event.button !== LEFT_BUTTON && event.button !== MIDDLE_BUTTON) {
      return
    }

    onEngage()

    if (
      event.button !== LEFT_BUTTON ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }

    event.preventDefault()
    setTimeout(() => {
      navigateWindowLocation(href)
    }, NAVIGATION_DELAY_MS)
  }
}
